import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type SelectPlayerPurpose = "compensation" | "confiscation";

export type SelectPlayerRow = {
  select: number;
  id: number;
  email: string;
  nickname: string;
  currency: string;
  balanceReal: number | null;
  status: string;
};

interface SelectPlayerState {
  purpose: SelectPlayerPurpose;
  userId: string;
  email: string;
  nickname: string;
  rows: SelectPlayerRow[];
  selectedUserId: number | null;
  activePage: number;
  totalItems: number;
  totalPages: number;
  hasSearched: boolean;
  searchError: boolean;
}

const initialState: SelectPlayerState = {
  purpose: "compensation",
  userId: "",
  email: "",
  nickname: "",
  rows: [],
  selectedUserId: null,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  hasSearched: false,
  searchError: false,
};

export const selectPlayerSlice = createSlice({
  name: "selectPlayer",
  initialState,
  reducers: {
    initSelectPlayer: (
      state,
      action: PayloadAction<{ purpose: SelectPlayerPurpose; reset?: boolean }>,
    ) => {
      const { purpose, reset = false } = action.payload;
      if (reset || state.purpose !== purpose) {
        Object.assign(state, { ...initialState, purpose });
        return;
      }
      state.purpose = purpose;
    },
    setSelectPlayerFilters: (
      state,
      action: PayloadAction<{
        userId?: string;
        email?: string;
        nickname?: string;
      }>,
    ) => {
      if (action.payload.userId !== undefined) {
        state.userId = action.payload.userId;
      }
      if (action.payload.email !== undefined) {
        state.email = action.payload.email;
      }
      if (action.payload.nickname !== undefined) {
        state.nickname = action.payload.nickname;
      }
    },
    setSelectPlayerResults: (
      state,
      action: PayloadAction<{
        rows: SelectPlayerRow[];
        totalItems: number;
        totalPages: number;
        activePage: number;
      }>,
    ) => {
      state.rows = action.payload.rows;
      state.totalItems = action.payload.totalItems;
      state.totalPages = action.payload.totalPages;
      state.activePage = action.payload.activePage;
      state.hasSearched = true;
      state.selectedUserId = null;
      state.searchError = false;
    },
    setSelectPlayerSelected: (
      state,
      action: PayloadAction<number | null>,
    ) => {
      state.selectedUserId = action.payload;
    },
    setSelectPlayerSearchError: (state, action: PayloadAction<boolean>) => {
      state.searchError = action.payload;
    },
    clearSelectPlayerResults: (state) => {
      state.rows = [];
      state.selectedUserId = null;
      state.activePage = 1;
      state.totalItems = 0;
      state.totalPages = 0;
      state.hasSearched = false;
      state.searchError = false;
    },
    resetSelectPlayer: () => initialState,
  },
});

export const {
  initSelectPlayer,
  setSelectPlayerFilters,
  setSelectPlayerResults,
  setSelectPlayerSelected,
  setSelectPlayerSearchError,
  clearSelectPlayerResults,
  resetSelectPlayer,
} = selectPlayerSlice.actions;
